import { FC, useMemo } from 'react';
import format from 'date-fns/format';
import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';

import { beautifyEnum, capitalizeName } from 'src/utils/utility';
import IconMail from 'src/assets/images/icons/icon-mail-colored.svg';
import IconInfo from 'src/assets/images/icons/info-circle-grey.svg';
import IconInfoDark from 'src/assets/images/icons/info-circle-dark.svg';
import IconCompleted from 'src/assets/images/icons/upload-completed.svg';

import { store } from 'src/infra/stores/store';
import { DELETE_MODE_KEY } from 'src/infra/stores/reducer/deleteModeSlice';
import { batch } from 'react-redux';
import { useDeleteInvoices } from 'src/use-cases/invoice/useDeleteInvoice';
import IconTrash from 'src/assets/images/icons/close-circle.svg';
import { NotificationCardStatus } from './CardStatus';
import { useNotificationSelector } from './NotificationContext';
import { INotificationCardProps } from './types';
import TransportMediumIcon from '../card/TransportMediumIcon';

interface StyledTooltipProps extends MuiTooltipProps {
  className?: string;
}

// Customize the tooltip
const Tooltip = styled(({ className, ...props }: StyledTooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)(() => ({
  zIndex: 999990,
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#202020!important',
    borderRadius: '8px!important',
    color: 'white',
  },
  '& .MuiTooltip-arrow': {
    color: '#202020!important',
  },
}));

// Helper function to render modality icons
const renderModalityIcons = (modalities: string[], notificationId: string) => {
  if (!modalities?.length) return null;

  return (
    <div className='flex items-center'>
      {modalities.map((item, index) => (
        <div key={`notif-${notificationId}-modality-${item}`} className={index > 0 ? '-ml-10' : ''}>
          <TransportMediumIcon medium={item as any} iconColor='#9F9F9F' size='sm' />
        </div>
      ))}
    </div>
  );
};

// Helper function to render service provider text
const renderServiceProviderText = (serviceProviderCodes: string[]) => {
  if (!serviceProviderCodes?.length) return null;

  return serviceProviderCodes.length > 1 ? `${serviceProviderCodes.length} SERVICE PROVIDERS` : beautifyEnum(serviceProviderCodes[0])?.toUpperCase();
};

// Helper function to render modality suffix
const renderModalitySuffix = (modalities: string[]) => {
  if (!modalities?.length) return null;

  return (
    <>
      <span className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
      <span className='text-10 xl:text-12 text-[#808080]'>{modalities.join(' & ')}</span>
    </>
  );
};

// Helper function to render other/info icon (for files that need review/resolution)
const renderUnderReviewIcon = () => (
  <div className='rounded-full flex items-center justify-center w-20 h-20' style={{ backgroundColor: '#EBEEF0', border: '1px solid #597282' }}>
    <img src={IconInfoDark} className='w-[14px]' alt='' />
  </div>
);

// Helper function to render tooltip items
const renderTooltipItem = (count: number, label: string, colorClass: string, hasBorder = false) => {
  if (count <= 0) return null;

  return (
    <div className='flex items-center gap-8'>
      <div className={`w-8 h-8 rounded-full ${colorClass} ${hasBorder ? 'border' : ''}`} />
      <strong className='text-white text-12'>
        {count} {label}
      </strong>
    </div>
  );
};

// Helper function to render service provider content
const renderServiceProviderContent = (serviceProviderCodes: string[], modalities: string[], notificationId: string, useUnderReviewFallback = false) => {
  const hasModalities = modalities?.length > 0;

  return (
    <div className='flex items-center gap-8'>
      {hasModalities ? renderModalityIcons(modalities, notificationId) : useUnderReviewFallback && renderUnderReviewIcon()}
      <strong className='text-14 xl:text-16 font-bold'>{renderServiceProviderText(serviceProviderCodes) || (useUnderReviewFallback && 'OTHER')}</strong>
      {hasModalities && renderModalitySuffix(modalities)}
    </div>
  );
};

export const NotificationCard: FC<INotificationCardProps> = ({ data }) => {
  const { setSelectedDetail, setIsShowDetail } = useNotificationSelector();

  const invoiceProcessed = useMemo(() => data?.totalFiles || 0, [data]);

  const { mutateAsync: deleteNotification } = useDeleteInvoices();

  const state = store.getState();
  const enableDeleteMode = state[DELETE_MODE_KEY]?.enable ?? false;

  const handleDelete = (batchId: string) => {
    deleteNotification({ batchIds: [batchId] })
      .then(() => {
        batch(() => {
          setSelectedDetail(null);
          setIsShowDetail(false);
        });
      })
      .catch((error) => {
        console.error('Error deleting notification:', error);
      });
  };

  return (
    <button
      type='button'
      className='px-8 group'
      onClick={() => {
        setSelectedDetail(data);
        setIsShowDetail(true);
      }}
    >
      <div className='px-14 py-14 xl:px-20 xl:py-16 rounded-8 group-hover:bg-[#F5F6FA] transition-all duration-300 flex items-center gap-24'>
        <div className='xl:w-[44px] xl:h-[44px] w-[36px] h-[36px] rounded-full bg-white border border-[#DFDFDF] flex items-center justify-center shadow-[0_2px_2px_0_rgba(0,0,0,0.06)]'>
          <img src={data?.notificationSourceType === 'EMAIL' ? IconMail : IconCompleted} alt='status' />
        </div>
        <div className='flex flex-col gap-4'>
          {(() => {
            // Show loading state for email received with missing data
            if (data?.notificationStatus === 'EMAIL_RECEIVED' && !data?.serviceProviderCodes?.length) {
              return <div className='w-[122px] h-[12px] rounded-[3px] bg-[#F5F6FA] my-4' />;
            }

            // Show "OTHERS" for batch processed with warnings ONLY when no service provider codes
            if (data?.notificationStatus === 'BATCH_PROCESSED_WITH_WARNINGS' && !data?.serviceProviderCodes?.length) {
              return renderServiceProviderContent([], [], data?.notificationId || '', true);
            }

            // Show service providers (for both normal and batch processed with warnings when we have codes)
            return renderServiceProviderContent(data?.serviceProviderCodes || [], data?.modalities || [], data?.notificationId || '');
          })()}
          <div className='flex items-center gap-4'>
            <span className='text-12 xl:text-14 font-medium'>
              {data?.newInvoices || 0} New {data?.newInvoices === 1 ? 'Invoice' : 'Invoices'} from {data?.totalFiles || 0}{' '}
              {data?.totalFiles === 1 ? 'file' : 'files'}
            </span>
            <Tooltip
              placement='bottom'
              arrow
              title={
                <div className='flex flex-col py-8 px-10 gap-3'>
                  {renderTooltipItem(data?.approvedInvoices, 'Ready for Payment', 'bg-primary')}
                  {renderTooltipItem(data?.flaggedInvoices, 'Flagged', 'bg-orange-30')}
                  {renderTooltipItem(data?.rejectedInvoices, 'Rejected', 'bg-red-40')}
                  {renderTooltipItem(data?.errorInvoices, 'Under Review', 'border-neutral-60', true)}
                  {renderTooltipItem(data?.processingInvoices, 'Processing', 'border-grey-20', true)}
                  {renderTooltipItem(data?.partialInvoices, 'Partially Completed', 'border-grey-20', true)}
                  {renderTooltipItem(data?.duplicateFiles, 'Duplicate', 'border-red-40', true)}
                  {renderTooltipItem(data?.resolvedAsNotAnInvoices, 'Invalid File', 'bg-[#6D7ACE]')}
                  {renderTooltipItem(data?.resolvedAsMissingRequiredFieldInvoices, 'Missing Required Field', 'border-red-40', true)}
                  {renderTooltipItem(data?.resolvedAsInconsistentShipmentDataInvoices, 'Inconsistent Shipment Data', 'border-red-40', true)}
                </div>
              }
            >
              <img src={IconInfo} alt='' />
            </Tooltip>
          </div>
          <p className='text-11 xl:text-12 text-[#808080] leading-snug break-words text-left'>
            {data?.notificationSourceType === 'EMAIL' ? 'Email received on' : 'Uploaded on'} {format(new Date(data?.uploadedOn), "dd-MMM-yyyy 'at' hh:mm a")}{' '}
            {data?.notificationSourceType === 'EMAIL' ? 'from' : 'by'}{' '}
            <strong className='font-medium text-neutral-80'>
              {data?.notificationSourceType === 'EMAIL'
                ? data?.uploadedBy // Show name as is for EMAIL
                : capitalizeName(data?.uploadedBy)}{' '}
              {/* Apply the utility for other cases */}
            </strong>
          </p>
        </div>

        <div className='ml-auto flex items-center gap-16 absolute right-20'>
          <NotificationCardStatus status={data?.notificationStatus} serviceProviderCodes={data?.serviceProviderCodes} />
          {enableDeleteMode && (
            <button
              type='button'
              className='w-32 h-32 text-white rounded-full hover:bg-red-20 transition-colors duration-200 flex items-center justify-center'
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(data?.batchId);
              }}
              title='Delete'
            >
              <img src={IconTrash} alt='icon-trash' className='w-28 h-28' />
            </button>
          )}
        </div>
      </div>
    </button>
  );
};
